import { type Ref, unref, watch } from 'vue';

interface UseFormPrefillOptions<T> {
  initialData: Ref<Partial<T> | undefined> | (() => Partial<T> | undefined);
  setValues: (values: T) => void; // VeeValidate setValues
  resetForm: (args: { values: T }) => void;
  isNew?: Ref<boolean>; // optional: only prefill for "new" records
  isDirty?: () => boolean; // optional: do not prefill if user has already edited
  forceOnChange?: boolean; // optional: force triggering onChange events when setting values
}

export function useFormPrefill<T extends Record<string, any>>(opts: UseFormPrefillOptions<T>) {
  const { initialData, setValues, isNew, forceOnChange = false } = opts;

  function getInitial(): Partial<T> | undefined {
    if (typeof initialData === 'function') {
      return initialData();
    }
    return unref(initialData);
  }

  /**
   * Watches initialData and applies it to the form whenever:
   * - initialData is available
   * - the record is new (if isNew is provided)
   * - the form is not dirty yet (if isDirty is provided)
   *
   * It may run multiple times (e.g. when initialData is filled step by step).
   */
  function watchInitialData(onApplied?: (merged: T) => void) {
    watch(
      () => getInitial(),
      (initial) => {
        // nothing to apply
        if (!initial) return;

        // only for new records (optional)
        if (isNew && isNew.value === false) return;

        // do not overwrite user input (optional)
        if (!forceOnChange && opts?.isDirty?.()) {
          return;
        }

        const merged = { ...(initial as T) };

        setValues(merged);

        onApplied?.(merged);
      },
      { immediate: true, deep: true },
    );
  }

  return {
    watchInitialData,
  };
}
